import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Trash2, Pencil, Check, X, Copy, ChevronDown, ChevronRight,
  Package, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiPut, apiDelete } from '../../../api/client';
import type { ServicePackage, PackageItem, ProductTemplate } from '../../../api/types';
import { ItemRow, AddItemRow } from './PackageItemRow';
import { EVENT_TYPES, marginPct, marginColor } from './constants';
import { inpSt, selSt, iconBtnStyle } from './sharedStyles';

export function PackageCard({ pkg, templates, isExpanded: propIsExpanded }: { pkg: ServicePackage; templates: ProductTemplate[]; isExpanded?: boolean }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(propIsExpanded ?? false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({
    name: pkg.name,
    description: pkg.description ?? '',
    eventType: pkg.eventType ?? '',
    discountPercentage: pkg.discountPercentage != null ? String(pkg.discountPercentage) : '',
    discountLabel: pkg.discountLabel ?? '',
  });
  const [showAddRow, setShowAddRow] = useState(false);

  // Compute totals
  const totalRevenue = pkg.items.reduce((s, i) => {
    const p = i.priceOverride ?? i.basePrice;
    return s + p;
  }, 0);
  const totalCost = pkg.items.reduce((s, i) => {
    if (i.costOverride != null) return s + i.costOverride;
    const tpl = i.productTemplateId ? templates.find(t => t.id === i.productTemplateId) : null;
    return s + (tpl?.baseCost ?? 0);
  }, 0);
  const totalMargin = totalRevenue - totalCost;
  const mPct = marginPct(totalCost, totalRevenue);

  const updateMutation = useMutation({
    mutationFn: (data: object) => apiPut(`/service-packages/${pkg.id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-packages'] }); setEditingHeader(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/service-packages/${pkg.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-packages'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => apiPost(`/service-packages/${pkg.id}/duplicate`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['service-packages'] }); toast.success('Pacchetto duplicato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => apiDelete(`/service-packages/${pkg.id}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-packages'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const moveItem = (item: PackageItem, dir: 'up' | 'down') => {
    const idx = pkg.items.findIndex(i => i.id === item.id);
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= pkg.items.length) return;
    const newOrder = [...pkg.items];
    [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
    Promise.all(newOrder.map((it, i) =>
      apiPut(`/service-packages/${pkg.id}/items/${it.id}`, { sortOrder: i })
    )).then(() => qc.invalidateQueries({ queryKey: ['service-packages'] }))
      .catch(err => toast.error('Errore riordinamento: ' + (err?.message || 'Riprova')));
  };

  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}
        onClick={() => !editingHeader && setExpanded(e => !e)}>
        {expanded ? <ChevronDown size={15} style={{ flexShrink: 0 }} /> : <ChevronRight size={15} style={{ flexShrink: 0 }} />}
        <Package size={15} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        {editingHeader ? (
          <div style={{ display: 'flex', gap: '0.375rem', flex: 1, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
            <input value={headerForm.name} onChange={e => setHeaderForm(f => ({ ...f, name: e.target.value }))} style={{ ...inpSt, flex: '1 0 160px' }} placeholder="Nome pacchetto" />
            <input value={headerForm.description} onChange={e => setHeaderForm(f => ({ ...f, description: e.target.value }))} style={{ ...inpSt, flex: '1 0 160px' }} placeholder="Descrizione (opzionale)" />
            <select value={headerForm.eventType} onChange={e => setHeaderForm(f => ({ ...f, eventType: e.target.value }))} style={selSt}>
              <option value="">Tutti i tipi</option>
              {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input type="number" min="0" max="100" step="0.1"
              value={headerForm.discountPercentage}
              onChange={e => setHeaderForm(f => ({ ...f, discountPercentage: e.target.value }))}
              style={{ ...inpSt, flex: '0 0 80px' }} placeholder="Sc. %" title="Sconto bundle (%)" />
            <input value={headerForm.discountLabel}
              onChange={e => setHeaderForm(f => ({ ...f, discountLabel: e.target.value }))}
              style={{ ...inpSt, flex: '1 0 120px' }} placeholder="Etichetta sconto" />
            <button onClick={() => updateMutation.mutate({
              name: headerForm.name,
              description: headerForm.description || null,
              eventType: headerForm.eventType || null,
              discountPercentage: headerForm.discountPercentage !== '' ? Number(headerForm.discountPercentage) : null,
              discountLabel: headerForm.discountLabel || null,
            })} style={iconBtnStyle}><Check size={14} color="var(--color-success)" /></button>
            <button onClick={() => setEditingHeader(false)} style={iconBtnStyle}><X size={14} color="var(--color-text-muted)" /></button>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{pkg.name}</span>
              {pkg.eventType && <span style={{ marginLeft: '0.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>({pkg.eventType})</span>}
              {pkg.discountPercentage != null && pkg.discountPercentage > 0 && (
                <span style={{
                  marginLeft: '0.5rem', fontSize: '10px', fontWeight: 700,
                  color: 'var(--color-primary)',
                  background: 'oklch(from var(--color-primary) l c h / 0.12)',
                  padding: '0.0625rem 0.35rem', borderRadius: 'var(--radius-interactive)',
                }}>
                  −{pkg.discountPercentage}%
                </span>
              )}
              {pkg.description && <span style={{ marginLeft: '0.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pkg.description}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                {pkg.items.length} voci · € {totalRevenue.toFixed(2)}
              </span>
              {totalCost > 0 && (
                <>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>
                    costo € {totalCost.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: marginColor(mPct), whiteSpace: 'nowrap' }}>
                    {mPct}% · € {totalMargin.toFixed(0)}
                  </span>
                </>
              )}
            </div>
            <button
              onClick={e => { e.stopPropagation(); updateMutation.mutate({ isActive: !pkg.isActive }); }}
              style={{ ...iconBtnStyle, color: pkg.isActive ? 'var(--color-success)' : 'var(--color-text-faint)' }}
              title={pkg.isActive ? 'Attivo — clicca per disattivare' : 'Inattivo — clicca per attivare'}
            >
              {pkg.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            </button>
            <button onClick={e => { e.stopPropagation(); setEditingHeader(true); setExpanded(true); }} style={iconBtnStyle} title="Modifica"><Pencil size={13} /></button>
            <button onClick={e => { e.stopPropagation(); duplicateMutation.mutate(); }} style={iconBtnStyle} title="Duplica" disabled={duplicateMutation.isPending}><Copy size={13} /></button>
            <button onClick={e => { e.stopPropagation(); if (confirm(`Eliminare "${pkg.name}"?`)) deleteMutation.mutate(); }} style={{ ...iconBtnStyle, color: 'var(--color-error)' }} title="Elimina"><Trash2 size={13} /></button>
          </>
        )}
      </div>

      {/* Items */}
      {expanded && (
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {pkg.items.length === 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', padding: '0.25rem 0' }}>Nessun prodotto. Aggiungine uno.</p>
          )}
          {pkg.items.map((item, idx) => (
            <ItemRow
              key={item.id} item={item} isFirst={idx === 0} isLast={idx === pkg.items.length - 1}
              packageId={pkg.id} templates={templates}
              onMoveUp={() => moveItem(item, 'up')}
              onMoveDown={() => moveItem(item, 'down')}
              onDelete={() => deleteItemMutation.mutate(item.id)}
            />
          ))}

          {showAddRow ? (
            <AddItemRow
              packageId={pkg.id}
              sortOrder={pkg.items.length}
              templates={templates}
              onDone={() => setShowAddRow(false)}
            />
          ) : (
            <button
              onClick={() => setShowAddRow(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.25rem', background: 'none', border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', padding: '0.375rem 0.75rem', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}
            >
              <Plus size={13} /> Aggiungi prodotto al pacchetto
            </button>
          )}
        </div>
      )}
    </div>
  );
}
